import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { UserRole } from '../../types'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Divider,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as EmergencyIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  getFamilyMembers,
  addFamilyMember,
  updateFamilyMember,
  deleteFamilyMember,
  loginAsFamilyMember,
  setEmergencyContact,
  type FamilyMember,
  type FamilyMemberFormData,
} from '../../services/familyService'
import { membershipApi } from '../../services/api'

// Import your Redux actions and types
import { login } from '../../store/slices/authSlice'
import { addNotification } from '../../store/slices/notificationSlice'
import {
  isValidEmail,
  isRequired,
  capitalizeName,
} from '../../utils/validation'
import dayjs from 'dayjs'
import { formatUSPhone, getUSPhoneErrorMessage } from '../../utils/phoneUtils'
import PhoneInput from '../ui/PhoneInput'

// Mock relationship options
export const relationshipOptions = [
  'Spouse',
  'Son',
  'Daughter',
  'Parent',
  'Sibling',
  'Other',
]

const initialFormData: FamilyMemberFormData = {
  firstName: '',
  lastName: '',
  relationship: '',
  dateOfBirth: null,
  email: '',
  phone: '',
  profilePhotoUrl: '',
  accessLevel: {
    viewAppointments: false,
    bookAppointments: false,
    viewBilling: false,
    receiveNotifications: false,
  },
}

// Define the response type for family login
interface FamilyLoginResponse {
  familyMember: {
    id: string
    _id: string
    firstName: string
    lastName: string
    email: string
    role: string
    // Add other fields as per your API response
  }
  access_token: string
  refresh_token: string
}

export const FamilyInfoSection: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null)
  const [formData, setFormData] =
    useState<FamilyMemberFormData>(initialFormData)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )
  const [loggingInMemberId, setLoggingInMemberId] = useState<string | null>(
    null
  )
  const [emailError, setEmailError] = useState<string>('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [phoneError, setPhoneError] = useState<string>('')
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState<FamilyMember | null>(
    null
  )
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeletingMember, setIsDeletingMember] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string>('')
  const [settingEmergencyContact, setSettingEmergencyContact] = useState<
    string | null
  >(null)
  const [emergencyContactDialogOpen, setEmergencyContactDialogOpen] =
    useState(false)
  const [memberToSetAsEmergencyContact, setMemberToSetAsEmergencyContact] =
    useState<FamilyMember | null>(null)

  // Fetch family members on component mount
  useEffect(() => {
    fetchFamilyMembers()
  }, [])

  const fetchFamilyMembers = async () => {
    try {
      setLoading(true)
      const response = await getFamilyMembers()

      const members = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : []

      setFamilyMembers(members)
    } catch (error) {
      console.error('Error fetching family members:', error)
      showSnackbar('Failed to fetch family members', 'error')
    } finally {
      setLoading(false)
    }
  }

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  const handleOpenDialog = (member?: FamilyMember) => {
    if (member) {
      setEditingMember(member)
      setFormData({
        firstName: member.firstName,
        lastName: member.lastName,
        relationship: member.relationship,
        dateOfBirth: member.dateOfBirth
          ? (() => {
              // Fix timezone issue by parsing date string in local timezone
              let dateStr = member.dateOfBirth
              // Remove time part if present
              if (dateStr.includes('T')) {
                dateStr = dateStr.split('T')[0]
              }
              // Remove time part if present in another format
              if (dateStr.includes(' ')) {
                dateStr = dateStr.split(' ')[0]
              }
              const [year, month, day] = dateStr.split('-').map(Number)
              // Create date in local timezone (month is 0-indexed in JavaScript Date)
              return new Date(year, month - 1, day)
            })()
          : null,
        email: member.email,
        phone: member.phone,
        profilePhotoUrl: member.profilePhotoUrl || '',
        accessLevel: {
          viewAppointments: member.accessLevel?.viewAppointments || false,
          bookAppointments: member.accessLevel?.bookAppointments || false,
          viewBilling: member.accessLevel?.viewBilling || false,
          receiveNotifications:
            member.accessLevel?.receiveNotifications || false,
        },
      })
      setProfilePhotoPreview(member.profilePhotoUrl || '')
    } else {
      setEditingMember(null)
      setFormData(initialFormData)
      setProfilePhotoPreview('')
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingMember(null)
    setFormData(initialFormData)
    setEmailError('')
    setHasAttemptedSubmit(false)
    setPhoneError('')
    setPhoneTouched(false)
    setProfilePhotoPreview('')
  }

  const handleInputChange = (field: keyof FamilyMemberFormData, value: any) => {
    // Automatically capitalize first letter for firstName and lastName
    let processedValue = value
    if (field === 'firstName' || field === 'lastName') {
      processedValue = capitalizeName(value as string)
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }))

    // Validate email in real-time as user types
    if (field === 'email') {
      const emailValue = value as string
      // Clear any server-side errors (like duplicate email) when user starts typing
      if (emailError && emailError.includes('already registered')) {
        setEmailError('')
      }

      if (emailValue.trim() === '') {
        // Only show required error if user has attempted to submit or has interacted with the field
        if (hasAttemptedSubmit) {
          setEmailError('Email is required')
        } else {
          setEmailError('')
        }
      } else if (!isValidEmail(emailValue)) {
        // Show format error immediately if email format is invalid
        setEmailError('Please enter a valid email address')
      } else {
        // Clear error if email is valid
        setEmailError('')
      }
    }
  }

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      phone: value,
    }))

    // Only validate if user has entered something (not required field)
    // Only validate and show error if:
    // 1. Field has been touched (blurred) AND value is invalid AND value is not empty
    // 2. OR phone number is complete (10 digits) and invalid
    const digitsOnly = value.replace(/\D/g, '')
    const isComplete = digitsOnly.length === 10
    const hasValue = value.trim().length > 0

    if (hasValue && (phoneTouched || isComplete)) {
      const error = getUSPhoneErrorMessage(value)
      if (error) {
        setPhoneError(error)
      } else {
        setPhoneError('')
      }
    } else {
      // Clear error if field is empty or hasn't been touched yet
      if (phoneError) {
        setPhoneError('')
      }
    }
  }

  const handleAccessLevelChange = (
    permission: keyof FamilyMemberFormData['accessLevel']
  ) => {
    setFormData(prev => ({
      ...prev,
      accessLevel: {
        ...prev.accessLevel,
        [permission]: !prev.accessLevel[permission],
      },
    }))
  }

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/avif']
    if (!validTypes.includes(file.type)) {
      showSnackbar(
        'Please select a valid image file (JPEG, JPG, PNG, AVIF)',
        'error'
      )
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image size should be less than 5MB', 'error')
      return
    }

    try {
      setUploadingPhoto(true)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      const response = await membershipApi.uploadProfilePhoto(file)

      if (!response.fileUrl) {
        showSnackbar('Failed to upload profile photo', 'error')
        return
      }

      setFormData(prev => ({
        ...prev,
        profilePhotoUrl: response.fileUrl,
      }))

      showSnackbar('Profile photo uploaded successfully', 'success')
    } catch (error: any) {
      console.error('Error uploading photo:', error)
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload profile photo'
      showSnackbar(errMsg, 'error')
      setProfilePhotoPreview('')
    } finally {
      setUploadingPhoto(false)
      if (event.target) event.target.value = ''
    }
  }

  const handleSave = async () => {
    setHasAttemptedSubmit(true)

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.relationship) {
      showSnackbar('Please fill in all required fields', 'error')
      return
    }

    // Validate email
    if (!isRequired(formData.email)) {
      setEmailError('Email is required')
      showSnackbar('Please fill in all required fields', 'error')
      return
    }

    if (!isValidEmail(formData.email)) {
      setEmailError('Please enter a valid email address')
      showSnackbar('Please enter a valid email address', 'error')
      return
    }

    setEmailError('')

    try {
      // Format date in local timezone to avoid timezone issues
      let formattedDate = ''
      if (formData.dateOfBirth) {
        const year = formData.dateOfBirth.getFullYear()
        const month = String(formData.dateOfBirth.getMonth() + 1).padStart(
          2,
          '0'
        )
        const day = String(formData.dateOfBirth.getDate()).padStart(2, '0')
        formattedDate = `${year}-${month}-${day}`
      }

      const memberData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        relationship: formData.relationship,
        dateOfBirth: formattedDate,
        email: formData.email,
        phone: formData.phone,
        profilePhotoUrl: formData.profilePhotoUrl || undefined,
        accessLevel: formData.accessLevel,
      }

      if (editingMember) {
        await updateFamilyMember(editingMember._id, memberData)
        showSnackbar('Family member updated successfully')
      } else {
        await addFamilyMember(memberData)
        showSnackbar('Family member added successfully')
      }

      await fetchFamilyMembers() // Refresh the list
      handleCloseDialog()
    } catch (error: any) {
      console.error('Error saving family member:', error)

      // Check if it's an axios error with response
      if (error.response) {
        const { status, data } = error.response

        // Handle duplicate email error (400 Bad Request)
        if (
          status === 400 &&
          data?.message &&
          (data.message.toLowerCase().includes('email already exists') ||
            data.message.toLowerCase().includes('patient with this email'))
        ) {
          setEmailError(
            'This email is already registered. Please use a different email address.'
          )
          showSnackbar(
            'A patient with this email already exists. Please use a different email address.',
            'error'
          )
          return
        }

        // Handle other API errors
        const errorMessage =
          data?.message ||
          (editingMember
            ? 'Failed to update family member'
            : 'Failed to add family member')
        showSnackbar(errorMessage, 'error')
      } else {
        // Handle network or other errors
        showSnackbar(
          editingMember
            ? 'Failed to update family member'
            : 'Failed to add family member',
          'error'
        )
      }
    }
  }

  const handleDelete = async (memberId: string): Promise<boolean> => {
    try {
      await deleteFamilyMember(memberId)
      showSnackbar('Family member removed successfully')
      await fetchFamilyMembers() // Refresh the list
      return true
    } catch (error) {
      console.error('Error deleting family member:', error)
      showSnackbar('Failed to remove family member', 'error')
      return false
    }
  }

  const handleOpenDeleteDialog = (member: FamilyMember) => {
    setMemberToDelete(member)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setMemberToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (!memberToDelete) {
      return
    }

    setIsDeletingMember(true)
    const success = await handleDelete(memberToDelete._id)
    setIsDeletingMember(false)

    if (success) {
      handleCloseDeleteDialog()
    }
  }

  const handleLogin = async (member: FamilyMember) => {
    try {
      setLoggingInMemberId(member._id)

      const response = await loginAsFamilyMember(member._id)

      // Transform API response to match your auth structure
      // const authUser = {
      //   id: response.familyMember?.id || response.familyMember?._id || member._id,
      //   firstName: response.familyMember?.firstName || member.firstName,
      //   lastName: response.familyMember?.lastName || member.lastName,
      //   email: response.familyMember?.email || member.email,
      //   role: 'client', // Set role as family to distinguish from main patient
      //   originalRole: 'family', // Store original role for switching back
      //   // Add any other fields your auth system expects
      // }
      const authUser = {
        id:
          response.familyMember?.id || response.familyMember?._id || member._id,
        firstName: response.familyMember?.firstName || member.firstName,
        lastName: response.familyMember?.lastName || member.lastName,
        email: response.familyMember?.email || member.email,
        role: UserRole.CLIENT, // ✅ now matches the enum
        originalRole: UserRole.CLIENT, // or UserRole.FAMILY if you define it later
        avatar: response.familyMember?.avatar || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        relationship: response.familyMember?.relationship,
      }
      localStorage.setItem(
        'profilePhotoUrl',
        response.familyMember?.profilePhotoUrl || ''
      )
      localStorage.setItem(
        'relationship',
        response.familyMember?.relationship || ''
      )

      // Store original patient info if available (for switching back)
      const currentAuth = localStorage.getItem('auth')
      if (currentAuth) {
        const currentUser = JSON.parse(currentAuth).user
        localStorage.setItem('originalPatient', JSON.stringify(currentUser))
      }

      // Dispatch login with access + refresh token
      dispatch(
        login({
          user: authUser,
          token: response.access_token,
          refreshToken: response.refresh_token,
        })
      )

      // Show success notification
      dispatch(
        addNotification({
          type: 'success',
          title: 'Switched Account!',
          message: `You are now logged in as ${authUser.firstName} ${authUser.lastName} (Family Member).`,
        })
      )

      // Redirect to dashboard or home page
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      console.error('Login as family member failed:', error)

      let errorMessage = 'Failed to login as family member. Please try again.'

      if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access. Please check your permissions.'
      } else if (error.response?.status === 404) {
        errorMessage = 'Family member not found.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.request) {
        errorMessage =
          'Unable to connect to server. Please check your internet connection.'
      }

      showSnackbar(errorMessage, 'error')

      dispatch(
        addNotification({
          type: 'error',
          title: 'Login Failed',
          message: errorMessage,
        })
      )
    } finally {
      setLoggingInMemberId(null)
    }
  }

  // Function to switch back to original patient account
  const handleOpenEmergencyContactDialog = (member: FamilyMember) => {
    setMemberToSetAsEmergencyContact(member)
    setEmergencyContactDialogOpen(true)
  }

  const handleCloseEmergencyContactDialog = () => {
    setEmergencyContactDialogOpen(false)
    setMemberToSetAsEmergencyContact(null)
  }

  const handleConfirmSetEmergencyContact = async () => {
    if (!memberToSetAsEmergencyContact) return

    try {
      setSettingEmergencyContact(memberToSetAsEmergencyContact._id)
      await setEmergencyContact(memberToSetAsEmergencyContact._id)
      showSnackbar(
        `${memberToSetAsEmergencyContact.firstName} ${memberToSetAsEmergencyContact.lastName} has been set as emergency contact`,
        'success'
      )
      // Refresh family members to get updated emergency contact status
      await fetchFamilyMembers()
      handleCloseEmergencyContactDialog()
    } catch (error) {
      console.error('Error setting emergency contact:', error)
      showSnackbar('Failed to set emergency contact', 'error')
    } finally {
      setSettingEmergencyContact(null)
    }
  }

  const switchBackToPatient = () => {
    const originalPatient = localStorage.getItem('originalPatient')
    const originalAuth = localStorage.getItem('originalAuth')

    if (originalPatient && originalAuth) {
      const patient = JSON.parse(originalPatient)
      const auth = JSON.parse(originalAuth)

      dispatch(
        login({
          user: patient,
          token: auth.token,
          refreshToken: auth.refreshToken,
        })
      )

      // Clear the stored original data
      localStorage.removeItem('originalPatient')
      localStorage.removeItem('originalAuth')

      dispatch(
        addNotification({
          type: 'success',
          title: 'Welcome Back!',
          message: `You are now back to your main account as ${patient.firstName} ${patient.lastName}.`,
        })
      )

      navigate('/dashboard', { replace: true })
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getAccessLevelSummary = (accessLevel: FamilyMember['accessLevel']) => {
    const permissions = []
    if (accessLevel?.viewAppointments) permissions.push('View')
    if (accessLevel?.bookAppointments) permissions.push('Book')
    if (accessLevel?.viewBilling) permissions.push('Billing')
    if (accessLevel?.receiveNotifications) permissions.push('Notifications')
    return permissions.length > 0 ? permissions.join(', ') : 'No permissions'
  }

  // Check if current user is a family member
  const isFamilyMember = () => {
    // You might want to get this from your Redux store
    const auth = localStorage.getItem('auth')
    if (auth) {
      const user = JSON.parse(auth).user
      return user.role === 'family'
    }
    return false
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Switch Back Button (show only when logged in as family member) */}
        {isFamilyMember() && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="h6" color="info.dark" gutterBottom>
              Logged in as Family Member
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={switchBackToPatient}
            >
              Switch Back to Main Account
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Family Information
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={isFamilyMember()} // Disable if logged in as family member
          >
            Add Family Member
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Adding a family member allows you to easily manage each other’s care
          in one place.
        </Typography>

        <Grid container spacing={3}>
          {familyMembers.map(member => (
            <Grid item xs={12} md={6} lg={4} key={member._id}>
              <Card
                elevation={2}
                sx={{
                  position: 'relative',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ p: 3, pt: 3 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
                  >
                    <Avatar
                      src={member.profilePhotoUrl || ''}
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        bgcolor: 'primary.light',
                      }}
                    >
                      {member.firstName[0]}
                      {member.lastName[0]}
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {member.firstName} {member.lastName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={member.relationship}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Tooltip title="Edit family member" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(member)}
                            sx={{ mr: 0.5 }}
                            disabled={isFamilyMember()} // Disable if logged in as family member
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove family member" arrow>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(member)}
                            disabled={isFamilyMember()} // Disable if logged in as family member
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ color: '#10B981', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {member.email || 'Not provided'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ color: '#10B981', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {member.phone
                          ? formatUSPhone(member.phone)
                          : 'Not provided'}
                      </Typography>
                    </Box>

                    {member.dateOfBirth &&
                    dayjs(member.dateOfBirth).isValid() ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CalendarIcon sx={{ color: '#10B981', fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">
                          Born {dayjs(member.dateOfBirth).format('MM/DD/YYYY')}
                        </Typography>
                      </Box>
                    ) : null}

                    {/* Uncomment if you want to show access level
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <SecurityIcon fontSize="small" color="action" />
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Access Level:
                        </Typography>
                        <Typography variant="body2">
                          {getAccessLevelSummary(member.accessLevel)}
                        </Typography>
                      </Box>
                    </Box>
                    */}
                  </Box>
                </CardContent>

                <Box sx={{ px: 3, pb: 2 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {member.isContactPersion ? (
                      <Box
                        sx={{
                          bgcolor: '#FEF2F2',
                          border: '1px solid #FECACA',
                          borderRadius: 1.5,
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <EmergencyIcon
                          sx={{ color: '#DC2626', fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: '#991B1B', fontWeight: 500 }}
                        >
                          This person is your designated emergency contact
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => handleOpenEmergencyContactDialog(member)}
                        disabled={isFamilyMember()}
                        startIcon={<EmergencyIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Set as Emergency Contact
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleLogin(member)}
                      disabled={
                        loggingInMemberId === member._id || isFamilyMember()
                      }
                      startIcon={
                        loggingInMemberId === member._id ? (
                          <CircularProgress size={16} />
                        ) : null
                      }
                    >
                      {loggingInMemberId === member._id
                        ? 'Logging in...'
                        : `Login as ${member.firstName}`}
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}

          {familyMembers.length === 0 && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No family members
                </Typography>
                {/* <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Add family members to give them access to your account
                  information and appointments.
                </Typography> */}
                {/* <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  disabled={isFamilyMember()}
                >
                  Add Your First Family Member
                </Button> */}
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Add/Edit Family Member Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingMember ? 'Edit Family Member' : 'Add Family Member'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Profile Photo Upload */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Avatar
                    src={profilePhotoPreview || formData.profilePhotoUrl}
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      bgcolor: 'primary.light',
                    }}
                  >
                    {!profilePhotoPreview && !formData.profilePhotoUrl && (
                      <PersonIcon sx={{ fontSize: 60 }} />
                    )}
                  </Avatar>
                  <input
                    accept="image/jpeg,image/jpg,image/png,image/avif"
                    style={{ display: 'none' }}
                    id="profile-photo-upload"
                    type="file"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                  />
                  <label htmlFor="profile-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={
                        uploadingPhoto ? (
                          <CircularProgress size={16} />
                        ) : (
                          <PhotoCameraIcon />
                        )
                      }
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto
                        ? 'Uploading...'
                        : profilePhotoPreview || formData.profilePhotoUrl
                          ? 'Change Photo'
                          : 'Upload Photo'}
                    </Button>
                  </label>
                  {formData.profilePhotoUrl && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, profilePhotoUrl: '' }))
                        setProfilePhotoPreview('')
                      }}
                      sx={{ mt: 1 }}
                    >
                      Remove Photo
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={formData.relationship}
                    label="Relationship"
                    onChange={e =>
                      handleInputChange('relationship', e.target.value)
                    }
                  >
                    {relationshipOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  maxDate={new Date()}
                  onChange={date => {
                    // Ensure date is created in local timezone
                    let localDate: Date | null = null
                    if (date) {
                      // Create date in local timezone to avoid timezone shifts
                      localDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      )
                    }
                    handleInputChange('dateOfBirth', localDate)
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  required
                  error={!!emailError}
                  helperText={emailError}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={() => {
                    setPhoneTouched(true)
                    // Only validate on blur if there's a value (not required field)
                    if (formData.phone.trim().length > 0) {
                      const error = getUSPhoneErrorMessage(formData.phone)
                      if (error) {
                        setPhoneError(error)
                      } else {
                        setPhoneError('')
                      }
                    } else {
                      setPhoneError('')
                    }
                  }}
                  error={!!phoneError}
                  helperText={phoneError}
                  showErrorOnBlur={false}
                />
              </Grid>
              {/* Uncomment if you want to show access permissions
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Access Permissions
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData?.accessLevel?.viewAppointments}
                        onChange={() =>
                          handleAccessLevelChange('viewAppointments')
                        }
                      />
                    }
                    label="View appointments and schedules"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.accessLevel.bookAppointments}
                        onChange={() =>
                          handleAccessLevelChange('bookAppointments')
                        }
                      />
                    }
                    label="Book and manage appointments"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.accessLevel.viewBilling}
                        onChange={() => handleAccessLevelChange('viewBilling')}
                      />
                    }
                    label="View billing and payment information"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.accessLevel.receiveNotifications}
                        onChange={() =>
                          handleAccessLevelChange('receiveNotifications')
                        }
                      />
                    }
                    label="Receive notifications and reminders"
                  />
                </FormGroup>
              </Grid>
              */}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={
                !formData.firstName ||
                !formData.lastName ||
                !formData.relationship ||
                !formData.email
              }
            >
              {editingMember ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={isDeletingMember ? undefined : handleCloseDeleteDialog}
        >
          <DialogTitle>Remove Family Member</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {memberToDelete
                ? `Are you sure you want to remove ${memberToDelete.firstName} ${memberToDelete.lastName} from your family members? They will no longer have access to your account.`
                : 'Are you sure you want to remove this family member?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              disabled={isDeletingMember}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeletingMember}
            >
              {isDeletingMember ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Emergency Contact Confirmation Dialog */}
        <Dialog
          open={emergencyContactDialogOpen}
          onClose={handleCloseEmergencyContactDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: '#FEE2E2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EmergencyIcon sx={{ color: '#DC2626', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Set Emergency Contact
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confirm your emergency contact person
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to set{' '}
                <strong>
                  {memberToSetAsEmergencyContact?.firstName}{' '}
                  {memberToSetAsEmergencyContact?.lastName}
                </strong>{' '}
                as your emergency contact?
              </Typography>
              <Box
                sx={{
                  bgcolor: '#F0F9FF',
                  border: '1px solid #BAE6FD',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#0369A1', fontWeight: 500, mb: 1 }}
                >
                  What does this mean?
                </Typography>
                <Typography variant="body2" sx={{ color: '#075985' }}>
                  This person will be contacted in case of medical emergencies
                  and will have access to important health information when
                  needed.
                </Typography>
              </Box>
              {familyMembers.some(
                m =>
                  m.isContactPersion &&
                  m._id !== memberToSetAsEmergencyContact?._id
              ) && (
                <Box
                  sx={{
                    bgcolor: '#FEF3C7',
                    border: '1px solid #FDE68A',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#92400E', fontWeight: 500 }}
                  >
                    Note: Setting this person as emergency contact will replace
                    the current emergency contact.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={handleCloseEmergencyContactDialog}
              disabled={settingEmergencyContact !== null}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSetEmergencyContact}
              variant="contained"
              color="error"
              disabled={settingEmergencyContact !== null}
              startIcon={
                settingEmergencyContact ===
                memberToSetAsEmergencyContact?._id ? (
                  <CircularProgress size={16} />
                ) : (
                  <EmergencyIcon />
                )
              }
              sx={{ textTransform: 'none', px: 3 }}
            >
              {settingEmergencyContact === memberToSetAsEmergencyContact?._id
                ? 'Setting...'
                : 'Confirm & Set'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}
